'use client';

import { useCallback, useState } from 'react';

/** How long a wrong tile stays red and shaking before it resets. */
const WRONG_FEEDBACK_MS = 500;

/**
 * The choose-one-of-N behaviour behind every tap puzzle (2b, 2f, 2h, 2j, 2k, 2o).
 *
 * ## Invariants:
 * - A wrong tap is shown and cleared, never counted. Why: there is no attempt budget here.
 *   Locking someone out after N misses would turn a mis-tap on a phone into a failed payment,
 *   and the challenge is a friction gate — its value is the interaction it forces, not the
 *   score it produces.
 * - Solving is idempotent: once solved, further taps are ignored. Why: `onSolved` triggers the
 *   API replay, and a double-tap on the correct tile would fire the request twice.
 */
export function useTapChallenge(answer: number, onSolved: () => void) {
  const [wrongIndex, setWrongIndex] = useState(-1);
  const [solved, setSolved] = useState(false);

  const pick = useCallback(
    (index: number) => {
      if (solved) return;
      if (index === answer) {
        setSolved(true);
        setWrongIndex(-1);
        onSolved();
        return;
      }
      setWrongIndex(index);
      window.setTimeout(() => setWrongIndex(-1), WRONG_FEEDBACK_MS);
    },
    [answer, onSolved, solved]
  );

  return { wrongIndex, solved, pick };
}
